import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TSuggest } from 'feature/registration/types'

import { IYandexMapState, MarkIconType, MarkType } from '../types'
import { IUserGeo } from '../types/IUserGeo'
import { TPoint } from '../types/TPoint'

const YANDEX_MAP_PREFIX = 'yandex_map_'
const PREFIX = (str: string) => `${YANDEX_MAP_PREFIX}${str}`

const DEFAULT_CENTER: [number, number] = [55.76, 37.64]

const getSessionMark = (type: MarkType) => {
  const mark = sessionStorage.getItem(PREFIX(type))
  return mark ? JSON.parse(mark) : null
}
const setSessionMark = (type: MarkType, coords: [number, number] | null) => {
  if (coords) {
    sessionStorage.setItem(PREFIX(type), JSON.stringify(coords))
  } else sessionStorage.removeItem(PREFIX(type))
}

const GEO_SESSION = 'geo_session'
const setSessionUserGeo = (geo: IUserGeo) => {
  sessionStorage.setItem(GEO_SESSION, JSON.stringify(geo))
}
const getSessionUserGeo = () => {
  const geo = sessionStorage.getItem(GEO_SESSION)
  return geo ? (JSON.parse(geo) as IUserGeo) : null
}

const initialState: IYandexMapState = {
  // центр карты (будет на нем при монтирование)
  center: DEFAULT_CENTER,
  // зум карты (будет на нем при монтирование)
  zoom: 14,
  // метки для карты, описаны в enum меток
  [MarkType.clickMark]: {
    coords: getSessionMark(MarkType.clickMark),
    icon: MarkIconType.building,
  },
  [MarkType.staticMark]: {
    coords: getSessionMark(MarkType.staticMark),
    icon: MarkIconType.building,
  },
  [MarkType.geolocationMark]: {
    coords: getSessionMark(MarkType.geolocationMark),
    icon: MarkIconType.geolocation,
  },
  [MarkType.serviceMarks]: null,
  // общие настройки
  setting: {
    isCanceledClick: false,
    geolocation: {
      // меняется ли значения поля ввода адреса после определения геопозиции
      changeInput: true,
    },
  },
  // данные геопозиции
  userGeo: getSessionUserGeo() || {
    geolocation: null,
    coords: null,
    address: null,
  },
  // построение маршрута
  routeSetting: {
    from: null,
    to: null,
    isLoading: false,
  },
}

// слайс для работы карты.
const yandexMapSlice = createSlice({
  name: 'yandexMap',
  initialState,
  reducers: {
    // установить центр карты, делать до монтирование карты (не плавно)
    // для плавного используем createAsyncThunk из папки actions
    setCenter: (state, action: PayloadAction<[number, number] | null>) => {
      if (action.payload && action.payload[0] && action.payload[1]) {
        state.center = action.payload
      } else state.center = DEFAULT_CENTER
    },
    setIsCanceledClick: (state, action: PayloadAction<boolean>) => {
      state.setting.isCanceledClick = action.payload
    },
    // полная чистка карты
    clearMap: state => {
      state.center = DEFAULT_CENTER
      state[MarkType.clickMark] = {
        coords: null,
        icon: MarkIconType.building,
      }
      state[MarkType.staticMark] = {
        coords: null,
        icon: MarkIconType.building,
      }
      state[MarkType.geolocationMark] = {
        coords: null,
        icon: MarkIconType.geolocation,
      }
      state[MarkType.serviceMarks] = null
      setSessionMark(MarkType.clickMark, null)
      setSessionMark(MarkType.staticMark, null)
      setSessionMark(MarkType.geolocationMark, null)
    },
    // установить координаты метки MarkType.clickMark
    setClickMarkCoords: (
      state,
      action: PayloadAction<[number, number] | null>,
    ) => {
      state[MarkType.clickMark].coords = action.payload
      state[MarkType.geolocationMark].coords = null
      setSessionMark(MarkType.clickMark, action.payload)
    },
    // установить иконку для метки MarkType.clickMark
    setClickMarkIcon: (state, action: PayloadAction<MarkIconType>) => {
      state[MarkType.clickMark].icon = action.payload
    },
    // установить координаты метки MarkType.staticMark
    setStaticMarkCoords: (
      state,
      action: PayloadAction<[number, number] | null>,
    ) => {
      state[MarkType.staticMark].coords = action.payload
      setSessionMark(MarkType.staticMark, action.payload)
    },
    // установить иконку для метки MarkType.staticMark
    setStaticMarkIcon: (state, action: PayloadAction<MarkIconType>) => {
      state[MarkType.staticMark].icon = action.payload
    },
    // установить координаты метки MarkType.geolocationMark
    setGeolocationMarkCoords: (
      state,
      action: PayloadAction<[number, number] | null>,
    ) => {
      state[MarkType.geolocationMark].coords = action.payload
      state[MarkType.clickMark].coords = null
      setSessionMark(MarkType.geolocationMark, action.payload)
    },
    // установить иконку для метки MarkType.geolocationMark
    setGeolocationMarkIcon: (state, action: PayloadAction<MarkIconType>) => {
      state[MarkType.geolocationMark].icon = action.payload
    },
    // установить массив меток серверных центров
    setServiceMarks: (state, action: PayloadAction<TPoint[] | null>) => {
      state[MarkType.serviceMarks] = action.payload
    },
    // устанавливаем настройки геолокации
    setGeolocationSetting: (
      state,
      action: PayloadAction<{
        changeInput: boolean
      }>,
    ) => {
      state.setting.geolocation = action.payload
    },
    // устанавливаем параметры построения маршрута
    setRouteSetting: (
      state,
      action: PayloadAction<{
        from?: [number, number] | null | string
        to?: [number, number] | null | string
        isLoading?: boolean
      }>,
    ) => {
      if (action.payload.from !== undefined)
        state.routeSetting.from = action.payload.from
      if (action.payload.to !== undefined)
        state.routeSetting.to = action.payload.to
      if (action.payload.isLoading !== undefined)
        state.routeSetting.isLoading = action.payload.isLoading
    },
    // ставим зум карты (не плавно, используем до монтирования)
    // для плавного используем createAsyncThunk из папки actions
    setMapZoom: (state, action: PayloadAction<number>) => {
      state.zoom = action.payload
    },
    // устанавливаем параметры геолокации пользователя
    setUserGeo: (
      state,
      action: PayloadAction<{
        geolocation?: GeolocationCoordinates | null
        coords?: [number, number] | null
        address?: TSuggest | null
      }>,
    ) => {
      if (action.payload.address !== undefined)
        state.userGeo.address = action.payload.address
      if (action.payload.coords !== undefined)
        state.userGeo.coords = action.payload.coords
      if (action.payload.geolocation !== undefined)
        state.userGeo.geolocation = action.payload.geolocation
      setSessionUserGeo(state.userGeo)
    },
  },
})

export const { actions, reducer } = yandexMapSlice
export { MarkIconType }
